/**
 * Type definitions for video player components
 */

import { VideoSource } from './utils';

/**
 * YouTube player state enum
 * These values match YouTube's PlayerState constants
 */
/* eslint-disable no-unused-vars */
export enum YouTubePlayerState {
  UNSTARTED = -1,
  ENDED = 0,
  PLAYING = 1,
  PAUSED = 2,
  BUFFERING = 3,
  CUED = 5,
}
/* eslint-enable no-unused-vars */

/**
 * Props for YouTubePlayer component
 */
export interface YouTubePlayerProps {
  videoUrl: string;
  onTimeUpdate?: (currentTime: number) => void;
  onStateChange?: (state: YouTubePlayerState) => void;
  onReady?: () => void;
  onError?: (error: Error) => void;
  autoplay?: boolean;
  startTime?: number; // Seek to specific time in seconds
  className?: string;
  width?: string | number;
  height?: string | number;
  controls?: boolean; // Show YouTube's native controls
  modestbranding?: boolean;
  rel?: boolean; // Show related videos
  showinfo?: boolean;
}

/**
 * Props for unified VideoPlayer component
 */
export interface VideoPlayerProps {
  videoUrl: string;
  videoSourceType?: VideoSource; // Optional override for source detection
  onTimeUpdate?: (currentTime: number) => void;
  onStateChange?: (state: YouTubePlayerState | 'playing' | 'paused' | 'ended') => void;
  onReady?: () => void;
  onError?: (error: Error) => void;
  autoplay?: boolean;
  startTime?: number;
  className?: string;
  width?: string | number;
  height?: string | number;
  controls?: boolean; // Show YouTube's native controls (passed to YouTubePlayer)
  modestbranding?: boolean;
  rel?: boolean;
  showinfo?: boolean;
}

/**
 * Video player methods interface
 * Unified interface for all video player implementations
 */
export interface VideoPlayerMethods {
  play: () => void;
  pause: () => void;
  seekTo: (seconds: number) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  setVolume: (volume: number) => void; // 0-100
  getVolume: () => number;
  setPlaybackRate: (rate: number) => void;
  getPlaybackRate: () => number;
  mute: () => void;
  unMute: () => void;
  isMuted: () => boolean;
}

